// TTS服务 - 支持队列播放和连续播放
export interface TTSVoice {
  id: string;
  name: string;
  gender: 'male' | 'female';
  language: string;
}

export interface TTSQueueItem {
  id: string;
  sessionId: string;
  text: string;
  voice: string;
  isPlaying: boolean;
  completed: boolean;
  synthesizing: boolean;
  audioUrl: string | null;
  error: string | null;
  timestamp: number;
}

export interface TTSOptions {
  voice?: string;
  onStart?: (item: TTSQueueItem) => void;
  onEnd?: (item: TTSQueueItem) => void;
  onError?: (item: TTSQueueItem, error: string) => void;
  onQueueEmpty?: (sessionId: string) => void;
  onSynthesizing?: (item: TTSQueueItem) => void;
  onSynthesizeComplete?: (item: TTSQueueItem) => void;
}

class TTSService {
  private queue: TTSQueueItem[] = [];
  private audioElements: Map<string, HTMLAudioElement> = new Map();
  private isProcessing = false;
  private queueIdCounter = 0;
  private readonly baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  private processedTexts: Map<string, Set<string>> = new Map(); // 记录每个会话已处理的文本
  
  // 默认语音配置
  private readonly defaultVoice = 'zh_female_shuangkuaisisi_moon_bigtts';
  
  // 可用语音列表
  private readonly voices: TTSVoice[] = [
    { id: 'zh_female_shuangkuaisisi_moon_bigtts', name: '双快思思', gender: 'female', language: 'zh' },
    { id: 'zh_male_jingqiangdeyuyin_moon_bigtts', name: '京腔的语音', gender: 'male', language: 'zh' },
    { id: 'zh_female_wennuandeaiyi_moon_bigtts', name: '温暖的爱意', gender: 'female', language: 'zh' },
    { id: 'zh_male_chunhoudecishen_moon_bigtts', name: '醇厚的磁声', gender: 'male', language: 'zh' },
    { id: 'zh_female_qingxindelinsheng_moon_bigtts', name: '清新的邻声', gender: 'female', language: 'zh' }
  ];

  /**
   * 获取可用语音列表
   */
  public getVoices(): TTSVoice[] {
    return [...this.voices];
  }

  /**
   * 验证文本是否包含可读内容
   * @param text 要验证的文本
   * @returns 是否包含可读内容
   */
  private isReadableText(text: string): boolean {
    if (!text || !text.trim()) {
      return false;
    }
    // 检查是否包含字母、数字、中文字符等可读内容
    return /[\w\u4e00-\u9fa5]/.test(text);
  }

  /**
   * 添加文本到播放队列
   * @param sessionId 会话ID，同一会话的文本会连续播放
   * @param text 要转换的文本
   * @param options 播放选项
   */
  public async addToQueue(
    sessionId: string, 
    text: string, 
    options: TTSOptions = {}
  ): Promise<string> {
    if (!this.isReadableText(text)) {
      console.log(`跳过无效文本: "${text}"`);
      return '';
    }

    const trimmedText = text.trim();
    console.log(`[TTS Service] 添加文本到队列: sessionId=${sessionId}, text="${trimmedText}"`);
    
    // 检查是否已经处理过相同的文本
    const sessionProcessedTexts = this.processedTexts.get(sessionId) || new Set();
    if (sessionProcessedTexts.has(trimmedText)) {
      console.log(`[TTS Service] 文本已存在，跳过重复添加: "${trimmedText}"`);
      return '';
    }

    // 记录已处理的文本
    sessionProcessedTexts.add(trimmedText);
    this.processedTexts.set(sessionId, sessionProcessedTexts);

    // 清理文本，按句子分割
    const sentences = this.splitIntoSentences(trimmedText);
    const addedIds: string[] = [];

    for (const sentence of sentences) {
      const trimmedSentence = sentence.trim();
      if (this.isReadableText(trimmedSentence)) {
        const queueItem: TTSQueueItem = {
          id: `tts_${++this.queueIdCounter}_${Date.now()}`,
          sessionId,
          text: trimmedSentence,
          voice: options.voice || this.defaultVoice,
          isPlaying: false,
          completed: false,
          synthesizing: false,
          audioUrl: null,
          error: null,
          timestamp: Date.now()
        };

        this.queue.push(queueItem);
        addedIds.push(queueItem.id);
        console.log(`[TTS Service] 队列项已添加: id=${queueItem.id}, text="${trimmedSentence}"`);

        // 开始合成语音
        this.synthesizeAudio(queueItem, options);
      } else {
        console.log(`跳过无效句子: "${trimmedSentence}"`);
      }
    }

    // 如果队列没有在处理，开始处理
    if (!this.isProcessing) {
      this.processQueue(options);
    }

    return addedIds.join(',');
  }

  /**
   * 停止指定会话的播放
   * @param sessionId 会话ID
   */
  public stopSession(sessionId: string): void {
    console.log(`[TTS Service] 停止会话 ${sessionId} 的语音播放`);
    
    // 停止该会话的所有音频
    this.queue
      .filter(item => item.sessionId === sessionId && item.isPlaying)
      .forEach(item => {
        const audio = this.audioElements.get(item.id);
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
          this.audioElements.delete(item.id);
        }
        item.isPlaying = false;
      });

    // 从队列中移除该会话的未完成项
    this.queue = this.queue.filter(item => 
      item.sessionId !== sessionId || item.completed
    );

    // 清理该会话的已处理文本记录
    this.processedTexts.delete(sessionId);
    console.log(`[TTS Service] 已清理会话 ${sessionId} 的处理记录`);

    // 清理音频资源
    this.cleanupCompletedItems();
  }

  /**
   * 停止所有播放
   */
  public stopAll(): void {
    console.log('[TTS Service] 停止所有语音播放');
    
    // 停止所有音频
    this.audioElements.forEach((audio, id) => {
      audio.pause();
      audio.currentTime = 0;
    });
    
    this.audioElements.clear();
    this.queue = [];
    this.isProcessing = false;
    this.processedTexts.clear();
    console.log('[TTS Service] 已清理所有处理记录');
  }

  /**
   * 获取队列状态
   */
  public getQueueStatus(sessionId?: string): {
    total: number;
    playing: number;
    completed: number;
    pending: number;
  } {
    const items = sessionId 
      ? this.queue.filter(item => item.sessionId === sessionId)
      : this.queue;

    return {
      total: items.length,
      playing: items.filter(item => item.isPlaying).length,
      completed: items.filter(item => item.completed).length,
      pending: items.filter(item => !item.completed && !item.isPlaying).length
    };
  }

  /**
   * 分割文本为句子
   */
  private splitIntoSentences(text: string): string[] {
    // 按标点符号分割句子，保留合理的长度
    const sentences = text
      .split(/([。！？；，.!?;,])/)
      .reduce((acc: string[], part: string, index: number, array: string[]) => {
        if (index % 2 === 0) {
          // 文本部分
          const nextPart = array[index + 1] || '';
          const sentence = (part + nextPart).trim();
          if (this.isReadableText(sentence)) {
            acc.push(sentence);
          }
        }
        return acc;
      }, []);

    return sentences.filter(s => this.isReadableText(s));
  }

  /**
   * 合成语音
   */
  private async synthesizeAudio(item: TTSQueueItem, options: TTSOptions): Promise<void> {
    // 再次验证文本有效性
    if (!this.isReadableText(item.text)) {
      console.warn(`跳过合成无效文本: "${item.text}"`);
      item.error = 'Invalid text for synthesis';
      item.synthesizing = false;
      return;
    }

    item.synthesizing = true;
    
    try {
      console.log(`开始合成语音: ${item.text}`);
      
      const response = await fetch(`${process.env.REACT_APP_BASE_URL||'http://localhost:8000/api/v1'}/tts/synthesize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({
          text: item.text,
          voice: item.voice
        })
      });

      const result = await response.json();
      
      if (result.success && result.audio_data) {
        // 将base64音频数据转换为可播放的URL
        const audioBlob = new Blob([
          Uint8Array.from(atob(result.audio_data), c => c.charCodeAt(0))
        ], { type: 'audio/mp3' });
        
        item.audioUrl = URL.createObjectURL(audioBlob);
        item.synthesizing = false;
        
        console.log(`语音合成完成: ${item.text}`);
        
        // 如果队列没有在处理，开始处理
        if (!this.isProcessing) {
          this.processQueue(options);
        }
      } else {
        throw new Error(result.error || '语音合成失败');
      }
    } catch (error) {
      console.error(`语音合成失败: ${item.text}`, error);
      item.error = error instanceof Error ? error.message : '语音合成失败';
      item.synthesizing = false;
      
      if (options.onError) {
        options.onError(item, item.error);
      }
    }
  }

  /**
   * 处理播放队列
   */
  private async processQueue(options: TTSOptions): Promise<void> {
    if (this.isProcessing) return;
    
    this.isProcessing = true;
    console.log('开始处理TTS队列');

    while (this.queue.length > 0) {
      // 按会话和时间戳排序，确保同一会话的内容按顺序播放
      this.queue.sort((a, b) => {
        if (a.sessionId !== b.sessionId) {
          return a.sessionId.localeCompare(b.sessionId);
        }
        return a.timestamp - b.timestamp;
      });

      // 找到下一个可以播放的项目
      const nextItem = this.queue.find(item => 
        !item.completed && 
        !item.isPlaying && 
        !item.synthesizing && 
        item.audioUrl && 
        !item.error
      );

      if (!nextItem) {
        // 等待合成完成或没有更多项目
        const hasSynthesizing = this.queue.some(item => item.synthesizing);
        if (hasSynthesizing) {
          await new Promise(resolve => setTimeout(resolve, 100));
          continue;
        } else {
          break;
        }
      }

      // 播放当前项目
      await this.playAudioItem(nextItem, options);
      
      // 标记为完成
      nextItem.completed = true;
      nextItem.isPlaying = false;

      // 清理已完成的项目
      this.cleanupCompletedItems();
    }

    this.isProcessing = false;
    console.log('TTS队列处理完成');

    // 通知队列为空
    const sessionIdSet = new Set(this.queue.map(item => item.sessionId));
    const sessionIds = Array.from(sessionIdSet);
    sessionIds.forEach(sessionId => {
      const sessionItems = this.queue.filter(item => item.sessionId === sessionId);
      if (sessionItems.length === 0 || sessionItems.every(item => item.completed)) {
        if (options.onQueueEmpty) {
          options.onQueueEmpty(sessionId);
        }
      }
    });
  }

  /**
   * 播放音频项目
   */
  private playAudioItem(item: TTSQueueItem, options: TTSOptions): Promise<void> {
    return new Promise((resolve) => {
      if (!item.audioUrl) {
        resolve();
        return;
      }

      console.log(`开始播放: ${item.text}`);
      
      item.isPlaying = true;
      
      if (options.onStart) {
        options.onStart(item);
      }

      const audio = new Audio(item.audioUrl);
      this.audioElements.set(item.id, audio);

      audio.onended = () => {
        console.log(`播放完成: ${item.text}`);
        item.isPlaying = false;
        this.audioElements.delete(item.id);
        
        if (options.onEnd) {
          options.onEnd(item);
        }
        
        resolve();
      };

      audio.onerror = () => {
        console.error(`播放失败: ${item.text}`);
        item.isPlaying = false;
        item.error = '播放失败';
        this.audioElements.delete(item.id);
        
        if (options.onError) {
          options.onError(item, '播放失败');
        }
        
        resolve();
      };

      audio.play().catch(error => {
        console.error(`播放失败: ${item.text}`, error);
        item.isPlaying = false;
        item.error = '播放失败: ' + error.message;
        this.audioElements.delete(item.id);
        
        if (options.onError) {
          options.onError(item, item.error);
        }
        
        resolve();
      });
    });
  }

  /**
   * 清理已完成的项目
   */
  private cleanupCompletedItems(): void {
    const completedItems = this.queue.filter(item => item.completed);
    
    // 保留最近的几个已完成项目用于显示，清理其他的
    if (completedItems.length > 10) {
      const itemsToRemove = completedItems
        .sort((a, b) => a.timestamp - b.timestamp)
        .slice(0, completedItems.length - 10);

      itemsToRemove.forEach(item => {
        if (item.audioUrl) {
          URL.revokeObjectURL(item.audioUrl);
        }
        const index = this.queue.indexOf(item);
        if (index > -1) {
          this.queue.splice(index, 1);
        }
      });
    }
  }

  /**
   * 单次播放文本（不使用队列，每次重新生成）
   * @param text 要播放的文本
   * @param options 播放选项
   * @returns Promise<void>
   */
  public async playOnce(
    text: string,
    options: TTSOptions = {}
  ): Promise<void> {
    if (!this.isReadableText(text)) {
      return;
    }

    const trimmedText = text.trim();

    // 创建临时播放项
    const playItem: TTSQueueItem = {
      id: `tts_once_${++this.queueIdCounter}_${Date.now()}`,
      sessionId: 'once',
      text: trimmedText,
      voice: options.voice || this.defaultVoice,
      isPlaying: false,
      completed: false,
      synthesizing: true,
      audioUrl: null,
      error: null,
      timestamp: Date.now()
    };

    console.log('DEBUG: ttsService playOnce - 开始处理');
    
    // 调用onSynthesizing回调，表示开始合成
    if (options.onSynthesizing) {
      console.log('DEBUG: ttsService 调用onSynthesizing回调');
      options.onSynthesizing(playItem);
    } else {
      console.log('DEBUG: ttsService onSynthesizing回调不存在');
    }

    try {
      console.log('DEBUG: ttsService 开始合成音频');
      // 合成音频
      await this.synthesizeAudioOnce(playItem, options);
      console.log('DEBUG: ttsService 音频合成完成');
      
      // 调用onSynthesizeComplete回调，表示合成完成
      if (options.onSynthesizeComplete) {
        console.log('DEBUG: ttsService 调用onSynthesizeComplete回调');
        options.onSynthesizeComplete(playItem);
      } else {
        console.log('DEBUG: ttsService onSynthesizeComplete回调不存在');
      }
      
      if (playItem.audioUrl && !playItem.error) {
        console.log('DEBUG: ttsService 开始播放音频');
        // 播放音频
        await this.playAudioItem(playItem, options);
        console.log('DEBUG: ttsService 音频播放完成');
      } else {
        console.log('DEBUG: ttsService 跳过播放，audioUrl:', playItem.audioUrl, 'error:', playItem.error);
        const errorMsg = playItem.error || '音频合成失败';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('DEBUG: ttsService 单次播放失败:', playItem.text, error);
      if (options.onError) {
        console.log('DEBUG: ttsService 调用onError回调');
        options.onError(playItem, error instanceof Error ? error.message : '播放失败');
      } else {
        console.log('DEBUG: ttsService onError回调不存在');
      }
      throw error;
    } finally {
      // 清理资源
      if (playItem.audioUrl) {
        URL.revokeObjectURL(playItem.audioUrl);
      }
    }
  }

  /**
   * 单次合成音频（不缓存）
   */
  private async synthesizeAudioOnce(item: TTSQueueItem, options: TTSOptions): Promise<void> {
    if (!this.isReadableText(item.text)) {
      console.warn(`跳过合成无效文本: "${item.text}"`);
      item.error = 'Invalid text for synthesis';
      return;
    }

    item.synthesizing = true;
    
    try {
      console.log(`开始单次合成语音: ${item.text}`);
      
      const response = await fetch(`${process.env.REACT_APP_BASE_URL||'http://localhost:8000/api/v1'}/tts/synthesize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}`,
        },
        body: JSON.stringify({
          text: item.text,
          voice: item.voice
        })
      });

      const result = await response.json();
      
      if (result.success && result.audio_data) {
        // 将base64音频数据转换为可播放的URL
        const audioBlob = new Blob([
          Uint8Array.from(atob(result.audio_data), c => c.charCodeAt(0))
        ], { type: 'audio/mp3' });
        
        item.audioUrl = URL.createObjectURL(audioBlob);
        item.synthesizing = false;
        
        console.log(`单次语音合成完成: ${item.text}`);
      } else {
        throw new Error(result.message || '语音合成失败');
      }
    } catch (error) {
      console.error(`单次语音合成失败: ${item.text}`, error);
      item.error = error instanceof Error ? error.message : '合成失败';
      item.synthesizing = false;
      
      if (options.onError) {
        options.onError(item, item.error);
      }
    }
  }

  /**
   * 获取认证Token
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('token');
  }
}

// 导出单例实例
export const ttsService = new TTSService();
export default ttsService;